import type { Language } from '../i18n';

import type { LandingId } from './slugs';

export interface Section {
    h: string;
    p: string;
}

export interface LandingCopy {
    /** The <title>; also the browser tab. */
    title: string;
    description: string;
    h1: string;
    intro: string;
    sections: Section[];
    faq: { q: string; a: string }[];
    cta: { title: string; body: string; button: string; to: string };
    /** Show the Chiron calculator under the intro. */
    calculator?: boolean;
}

/** Words used by the calculator, the sign pages, the card pages and the link lists. */
export interface SeoUi {
    siteName: string;
    calc: {
        title: string;
        date: string;
        button: string;
        result: string;
        retrograde: string;
        retrogradeNote: string;
        edge: string;
        range: string;
        sign: string;
        moreAbout: string;
    };
    sign: {
        title: string;
        description: string;
        h1: string;
        woundTitle: string;
        giftTitle: string;
        whenTitle: string;
        when: string;
        whenNone: string;
        present: string;
        allSigns: string;
        cta: { title: string; body: string; button: string };
    };
    card: {
        title: string;
        description: string;
        keywords: string;
        upright: string;
        reversed: string;
        advice: string;
        more: string;
        indexTitle: string;
        indexDescription: string;
        indexIntro: string;
        major: string;
        suits: Record<string, string>;
        cta: { title: string; body: string; button: string };
    };
    explore: string;
    back: string;
}

export const LANDING_ORDER: LandingId[] = [
    'free-birth-chart',
    'whats-your-birth-chart',
    'chiron-sign-calculator',
    'chiron-in-astrology',
];

const en: Record<LandingId, LandingCopy> = {
    'free-birth-chart': {
        title: 'Free Birth Chart Calculator: Your Natal Chart Online | Arcanae',
        description:
            'Make a free birth chart with your date, time and place of birth: Sun, Moon, rising sign, planets, houses, aspects and Chiron, with a plain-language reading.',
        h1: 'Free birth chart calculator',
        intro: 'A birth chart, or natal chart, is a map of the sky at the moment and place you were born. Enter your date, time and city and Arcanae draws the wheel, places every planet and Chiron in its sign and house, and explains each in plain language. Your details stay on your device.',
        sections: [
            {
                h: 'What your free birth chart shows',
                p: 'Your chart shows the Sun, Moon and planets in the signs and houses they occupied at birth, the angles of the chart (Ascendant and Midheaven), the north node, Chiron and the aspects between them. Each placement comes with a short reading, so you can see how the pieces fit together instead of reading a list of symbols.',
            },
            {
                h: 'What you need to make it',
                p: 'You need the date and the city of your birth. The exact time of birth gives you the rising sign, the houses and the Moon’s precise degree; without it, you still get your planets in signs, and the chart is cast for noon. If you do not know the time, a birth certificate or a family member may remember it.',
            },
            {
                h: 'How accurate is it?',
                p: 'Planet positions are calculated from astronomical ephemerides, and houses use the Placidus system, with whole-sign houses as a fallback near the poles. Time zones and daylight saving time of the birthplace are applied for the date you give, including historic changes.',
            },
        ],
        faq: [
            {
                q: 'Is this birth chart really free?',
                a: 'Yes. You can make as many charts as you like. A short video opens each new chart, and an optional lifetime unlock removes it.',
            },
            {
                q: 'Is my birth data stored on a server?',
                a: 'No. Your charts are saved on your own device only, and you can delete them whenever you wish.',
            },
            {
                q: 'What if I do not know my birth time?',
                a: 'Tick “I don’t know the time”. You will get your planets in signs, but not the rising sign, houses or angles, and the Moon’s sign may be uncertain on days it changes sign.',
            },
        ],
        cta: {
            title: 'Make your birth chart',
            body: 'Date, time and city: a minute, and your chart opens with a reading for each placement.',
            button: 'Open the birth chart',
            to: '/birth-chart',
        },
    },
    'whats-your-birth-chart': {
        title: 'What’s Your Birth Chart? Find Your Sun, Moon and Rising Sign | Arcanae',
        description:
            'Wondering what your birth chart says? Learn what the Sun, Moon, rising sign and Chiron in a natal chart mean, and get yours free in a minute.',
        h1: 'What is your birth chart?',
        intro: 'Your birth chart is a snapshot of the sky from the place where you were born, drawn as a wheel of twelve signs and twelve houses. It is often described as a portrait of the themes you were born into, something to reflect on rather than a verdict.',
        sections: [
            {
                h: 'The big three: Sun, Moon and rising sign',
                p: 'The Sun sign is the one most people know: your core identity and what energises you. The Moon sign describes how you feel and what makes you feel safe. The rising sign, or Ascendant, is how you meet the world and how others first see you. Together they give a first sketch of a person.',
            },
            {
                h: 'Beyond the big three',
                p: 'The other planets add the way you think (Mercury), love (Venus) and act (Mars), and the slower planets add themes that span a generation. Chiron, the wounded healer, points to a tender spot that can become a source of understanding for others. The houses show which area of life each of these touches.',
            },
            {
                h: 'How to read the wheel',
                p: 'Start with the Sun, Moon and rising sign, then look at which house holds most of the planets. Notice what resonates and what does not: a chart is a set of questions to explore, and you are the one who decides what fits.',
            },
        ],
        faq: [
            {
                q: 'How do I find my rising sign?',
                a: 'You need your exact time and place of birth. Enter them in the birth chart and the rising sign is shown with the Sun and Moon.',
            },
            {
                q: 'Can two people have the same chart?',
                a: 'Only if they were born at nearly the same time and place. The Moon and the rising sign change quickly, so even siblings born a few hours apart can differ.',
            },
        ],
        cta: {
            title: 'See your chart',
            body: 'Find your Sun, Moon, rising sign and Chiron in one place.',
            button: 'Find my birth chart',
            to: '/birth-chart',
        },
    },
    'chiron-sign-calculator': {
        title: 'What’s My Chiron Sign? Free Chiron Sign Calculator | Arcanae',
        description:
            'Find your Chiron sign with a free calculator: enter your birth date to see your Chiron sign, degree and whether Chiron was retrograde, with a reading.',
        h1: 'What’s my Chiron sign? Chiron sign calculator',
        intro: 'Chiron is the wounded healer, a small body between Saturn and Uranus that points to where you may have felt hurt and where you can help others through what you have learned. Enter your birth date to find your Chiron sign.',
        calculator: true,
        sections: [
            {
                h: 'What is Chiron in astrology?',
                p: 'Chiron was discovered in 1977 and named for the centaur of Greek myth who taught healing while carrying a wound that would not close. In a chart, it is read as a sensitive spot, often tied to early experience, and as a gift for understanding others who have had a similar experience.',
            },
            {
                h: 'How the calculator works',
                p: 'Chiron takes about fifty years to circle the Sun and spends from about a year and a half to eight years in each sign, so people born in the same few years share a Chiron sign. The calculator uses NASA JPL ephemeris data for the years 1900 to 2050. For births within a day of a sign change, add your birth time and place in the full chart.',
            },
            {
                h: 'What your Chiron sign and house mean',
                p: 'The sign shows how the tender spot tends to show up; the house, which needs your time and place of birth, shows where in life. Neither one is a sentence. Many people find that the sign names something familiar and that the house points to where they have learned to care for others.',
            },
        ],
        faq: [
            {
                q: 'Do I need my birth time to know my Chiron sign?',
                a: 'Usually not. Chiron moves slowly, so the date is enough unless you were born within a day or so of a sign change. The house does need the birth time and place.',
            },
            {
                q: 'What does retrograde Chiron mean?',
                a: 'Chiron appears to move backwards for about five months of each year, as the Earth passes it. Some people read it as an inward turn, working through the tender spot privately; others see no difference. See what resonates with you.',
            },
        ],
        cta: {
            title: 'See Chiron in your full chart',
            body: 'Add your birth time and place to see Chiron’s house and how it sits among your planets.',
            button: 'Open the birth chart',
            to: '/birth-chart',
        },
    },
    'chiron-in-astrology': {
        title: 'Chiron in Astrology: The Wounded Healer Explained | Arcanae',
        description:
            'What Chiron means in astrology: the wounded healer, its myth, its discovery, its signs and houses, and how to find yours in your birth chart.',
        h1: 'Chiron in astrology: the wounded healer',
        intro: 'Chiron is a small body that orbits between Saturn and Uranus. Astrologers read it as the wounded healer: the place in a chart where an old hurt meets a gift for helping others. It is one of the most discussed points in modern astrology.',
        sections: [
            {
                h: 'The myth of Chiron',
                p: 'In Greek myth, Chiron was a centaur, wiser and gentler than his kind, who taught heroes such as Asclepius and Achilles. Wounded by a poisoned arrow, he could not die and could not heal himself, yet he went on teaching. Astrology borrows the image: a wound that cannot be fully erased, and wisdom that comes from living with it.',
            },
            {
                h: 'Chiron through the signs',
                p: 'The sign of Chiron colours the way a tender spot appears: around courage in Aries, worth in Taurus, being heard in Gemini, belonging in Cancer, being seen in Leo, usefulness in Virgo, closeness in Libra, trust in Scorpio, meaning in Sagittarius, achievement in Capricorn, difference in Aquarius and boundaries in Pisces.',
            },
            {
                h: 'Chiron through the houses',
                p: 'The house of Chiron shows where in life you may feel it most: from your self-image in the first house to the hidden and unconscious in the twelfth. The same house is often where people find they can help others.',
            },
            {
                h: 'Chiron return',
                p: 'Chiron returns to its birth sign around the age of 50, a time many describe as a turning point of looking back at old hurts and what they have taught. It is a moment people choose to reflect on in their own way.',
            },
        ],
        faq: [
            {
                q: 'Is Chiron a planet?',
                a: 'No. It is a small body, classified as a minor planet and comet, which is why many astrologers call it a point or an asteroid-like body. It is still shown in birth charts.',
            },
            {
                q: 'How long does Chiron stay in a sign?',
                a: 'Between about a year and a half and eight years, because its orbit is elongated. It first moved into Taurus in 2026, dipped back into Aries, and settles in Taurus in 2027.',
            },
        ],
        calculator: true,
        cta: {
            title: 'Find Chiron in your chart',
            body: 'Your birth chart places Chiron in its sign and house and offers a reading to reflect on.',
            button: 'Open the birth chart',
            to: '/birth-chart',
        },
    },
};

const pt: Record<LandingId, LandingCopy> = {
    'free-birth-chart': {
        title: 'Mapa Astral Grátis: Calcule Seu Mapa Natal Online | Arcanae',
        description:
            'Faça seu mapa astral grátis com data, hora e local de nascimento: Sol, Lua, ascendente, planetas, casas, aspectos e Quíron, com uma leitura em linguagem simples.',
        h1: 'Mapa astral grátis',
        intro: 'O mapa astral, ou mapa natal, é um retrato do céu no momento e no lugar em que você nasceu. Informe data, hora e cidade e o Arcanae desenha a roda, coloca cada planeta e Quíron no signo e na casa e explica cada um em linguagem simples. Seus dados ficam no seu aparelho.',
        sections: [
            {
                h: 'O que seu mapa astral grátis mostra',
                p: 'O mapa mostra o Sol, a Lua e os planetas nos signos e casas em que estavam ao nascer, os ângulos do mapa (Ascendente e Meio do Céu), o nodo norte, Quíron e os aspectos entre eles. Cada posição vem com uma leitura curta, para você ver como as peças se encaixam em vez de ler uma lista de símbolos.',
            },
            {
                h: 'O que você precisa para fazê-lo',
                p: 'Você precisa da data e da cidade de nascimento. A hora exata dá o ascendente, as casas e o grau preciso da Lua; sem ela, você ainda recebe os planetas nos signos, e o mapa é feito para o meio-dia. Se não sabe a hora, a certidão de nascimento ou alguém da família pode lembrar.',
            },
            {
                h: 'Qual a precisão?',
                p: 'As posições dos planetas vêm de efemérides astronômicas, e as casas usam o sistema Placidus, com casas por signo inteiro como alternativa perto dos polos. O fuso e o horário de verão do local de nascimento são aplicados para a data informada, inclusive mudanças históricas.',
            },
        ],
        faq: [
            {
                q: 'O mapa astral é mesmo grátis?',
                a: 'Sim. Você pode fazer quantos mapas quiser. Um vídeo curto abre cada novo mapa, e um desbloqueio vitalício opcional o remove.',
            },
            {
                q: 'Meus dados de nascimento ficam em algum servidor?',
                a: 'Não. Seus mapas ficam salvos só no seu aparelho, e você pode apagá-los quando quiser.',
            },
            {
                q: 'E se eu não souber a hora de nascimento?',
                a: 'Marque “Não sei a hora”. Você terá os planetas nos signos, mas não o ascendente, as casas e os ângulos, e o signo da Lua pode ficar incerto nos dias em que ela muda de signo.',
            },
        ],
        cta: {
            title: 'Faça seu mapa astral',
            body: 'Data, hora e cidade: um minuto, e seu mapa abre com uma leitura de cada posição.',
            button: 'Abrir o mapa astral',
            to: '/birth-chart',
        },
    },
    'whats-your-birth-chart': {
        title: 'Qual É o Seu Mapa Astral? Descubra Sol, Lua e Ascendente | Arcanae',
        description:
            'Quer saber o que seu mapa astral diz? Entenda o que significam Sol, Lua, ascendente e Quíron no mapa natal e faça o seu grátis em um minuto.',
        h1: 'Qual é o seu mapa astral?',
        intro: 'Seu mapa astral é uma imagem do céu visto do lugar onde você nasceu, desenhada como uma roda de doze signos e doze casas. Costuma ser descrito como um retrato dos temas em que você nasceu, algo para refletir, e não um veredito.',
        sections: [
            {
                h: 'Os três grandes: Sol, Lua e ascendente',
                p: 'O signo solar é o que a maioria das pessoas conhece: sua identidade central e o que o energiza. O signo da Lua descreve como você sente e o que lhe traz segurança. O ascendente é como você encontra o mundo e como os outros o veem de início. Juntos, dão um primeiro esboço de uma pessoa.',
            },
            {
                h: 'Além dos três grandes',
                p: 'Os outros planetas acrescentam o modo como você pensa (Mercúrio), ama (Vênus) e age (Marte), e os planetas lentos trazem temas de uma geração inteira. Quíron, o curador ferido, aponta para um ponto sensível que pode se tornar fonte de compreensão para os outros. As casas mostram qual área da vida cada um toca.',
            },
            {
                h: 'Como ler a roda',
                p: 'Comece pelo Sol, pela Lua e pelo ascendente e depois veja qual casa reúne mais planetas. Note o que ressoa e o que não: um mapa é um conjunto de perguntas a explorar, e quem decide o que serve é você.',
            },
        ],
        faq: [
            {
                q: 'Como descubro meu ascendente?',
                a: 'Você precisa da hora e do local exatos de nascimento. Informe-os no mapa astral e o ascendente aparece junto com o Sol e a Lua.',
            },
            {
                q: 'Duas pessoas podem ter o mesmo mapa?',
                a: 'Só se nascerem quase na mesma hora e lugar. A Lua e o ascendente mudam rápido, então até irmãos nascidos com algumas horas de diferença podem ter mapas distintos.',
            },
        ],
        cta: {
            title: 'Veja seu mapa',
            body: 'Encontre Sol, Lua, ascendente e Quíron em um só lugar.',
            button: 'Descobrir meu mapa astral',
            to: '/birth-chart',
        },
    },
    'chiron-sign-calculator': {
        title: 'Qual É o Meu Signo de Quíron? Calculadora Grátis | Arcanae',
        description:
            'Descubra seu signo de Quíron com uma calculadora grátis: informe a data de nascimento e veja o signo, o grau e se Quíron estava retrógrado, com uma leitura.',
        h1: 'Qual é o meu Quíron? Calculadora de signo de Quíron',
        intro: 'Quíron é o curador ferido, um pequeno corpo entre Saturno e Urano que aponta onde você pode ter se sentido ferido e onde pode ajudar os outros a partir do que aprendeu. Informe sua data de nascimento para descobrir seu signo de Quíron.',
        calculator: true,
        sections: [
            {
                h: 'O que é Quíron na astrologia?',
                p: 'Quíron foi descoberto em 1977 e recebeu o nome do centauro da mitologia grega que ensinava a cura carregando uma ferida que não fechava. No mapa, é lido como um ponto sensível, muitas vezes ligado a experiências da infância, e como um dom para compreender quem passou por algo parecido.',
            },
            {
                h: 'Como a calculadora funciona',
                p: 'Quíron leva cerca de cinquenta anos para dar uma volta no Sol e passa de um ano e meio a oito anos em cada signo, então pessoas nascidas nos mesmos anos dividem o signo de Quíron. A calculadora usa dados de efemérides do JPL da NASA para os anos de 1900 a 2050. Para nascimentos a um dia de uma mudança de signo, informe hora e local no mapa completo.',
            },
            {
                h: 'O que significam o signo e a casa de Quíron',
                p: 'O signo mostra como o ponto sensível tende a aparecer; a casa, que pede hora e local de nascimento, mostra em que área da vida. Nenhum dos dois é uma sentença. Muita gente acha que o signo nomeia algo familiar e que a casa aponta onde aprendeu a cuidar dos outros.',
            },
        ],
        faq: [
            {
                q: 'Preciso da hora de nascimento para saber meu signo de Quíron?',
                a: 'Em geral não. Quíron se move devagar, então a data basta, a menos que você tenha nascido a um dia ou pouco mais de uma mudança de signo. A casa exige hora e local.',
            },
            {
                q: 'O que significa Quíron retrógrado?',
                a: 'Quíron parece andar para trás por cerca de cinco meses a cada ano, conforme a Terra o ultrapassa. Há quem leia isso como um voltar-se para dentro, lidando em privado com o ponto sensível; outros não veem diferença. Veja o que ressoa em você.',
            },
        ],
        cta: {
            title: 'Veja Quíron no mapa completo',
            body: 'Acrescente hora e local de nascimento para ver a casa de Quíron e como ele se relaciona com seus planetas.',
            button: 'Abrir o mapa astral',
            to: '/birth-chart',
        },
    },
    'chiron-in-astrology': {
        title: 'Quíron na Astrologia: O Curador Ferido Explicado | Arcanae',
        description:
            'O que Quíron significa na astrologia: o curador ferido, o mito, a descoberta, os signos e as casas, e como encontrar o seu no mapa astral.',
        h1: 'Quíron na astrologia: o curador ferido',
        intro: 'Quíron é um pequeno corpo que orbita entre Saturno e Urano. Astrólogos o leem como o curador ferido: o lugar do mapa onde uma dor antiga encontra um dom para ajudar os outros. É um dos pontos mais comentados da astrologia moderna.',
        sections: [
            {
                h: 'O mito de Quíron',
                p: 'Na mitologia grega, Quíron era um centauro, mais sábio e gentil que os da sua espécie, que ensinou heróis como Asclépio e Aquiles. Ferido por uma flecha envenenada, não podia morrer nem curar a si mesmo, e ainda assim continuou a ensinar. A astrologia toma a imagem: uma ferida que não se apaga por completo e a sabedoria que vem de conviver com ela.',
            },
            {
                h: 'Quíron nos signos',
                p: 'O signo de Quíron colore o jeito do ponto sensível: coragem em Áries, valor em Touro, ser ouvido em Gêmeos, pertencimento em Câncer, ser visto em Leão, utilidade em Virgem, proximidade em Libra, confiança em Escorpião, sentido em Sagitário, conquista em Capricórnio, diferença em Aquário e limites em Peixes.',
            },
            {
                h: 'Quíron nas casas',
                p: 'A casa de Quíron mostra em que área da vida você pode senti-lo mais: da autoimagem na casa 1 ao oculto e ao inconsciente na casa 12. Muitas vezes é nessa mesma casa que as pessoas descobrem que podem ajudar os outros.',
            },
            {
                h: 'Retorno de Quíron',
                p: 'Quíron volta ao signo do nascimento por volta dos 50 anos, momento que muitos descrevem como uma virada de olhar para mágoas antigas e para o que elas ensinaram. É uma ocasião que cada pessoa escolhe refletir à sua maneira.',
            },
        ],
        faq: [
            {
                q: 'Quíron é um planeta?',
                a: 'Não. É um pequeno corpo, classificado como planeta menor e cometa, por isso muitos astrólogos o chamam de ponto ou corpo semelhante a um asteroide. Ainda assim, aparece nos mapas astrais.',
            },
            {
                q: 'Quanto tempo Quíron fica em um signo?',
                a: 'Entre cerca de um ano e meio e oito anos, porque sua órbita é alongada. Ele entrou em Touro em 2026, voltou a Áries e se firma em Touro em 2027.',
            },
        ],
        calculator: true,
        cta: {
            title: 'Encontre Quíron no seu mapa',
            body: 'Seu mapa astral coloca Quíron no signo e na casa e oferece uma leitura para refletir.',
            button: 'Abrir o mapa astral',
            to: '/birth-chart',
        },
    },
};

export const landingCopy = (language: Language, id: LandingId): LandingCopy =>
    (language === 'pt-BR' ? pt : en)[id];

const uiEn: SeoUi = {
    siteName: 'Arcanae',
    calc: {
        title: 'Chiron sign calculator',
        date: 'Date of birth',
        button: 'Find my Chiron sign',
        result: 'Your Chiron is in',
        retrograde: 'Chiron was retrograde at your birth.',
        retrogradeNote:
            'Some people read this as working through the tender spot privately; see what resonates with you.',
        edge: 'Chiron changed sign around your birth date, so your sign could be either. Add your birth time and place in the full chart.',
        range: 'Chiron data covers 1900 to 2050.',
        sign: 'About Chiron in {sign}',
        moreAbout: 'Read more',
    },
    sign: {
        title: 'Chiron in {sign}: Meaning, Dates and Healing | Arcanae',
        description:
            'Chiron in {sign} in the birth chart: the wound, the gift, the years Chiron was in {sign}, and how to find your own Chiron sign.',
        h1: 'Chiron in {sign}',
        woundTitle: 'The tender spot',
        giftTitle: 'The gift',
        whenTitle: 'When was Chiron in {sign}?',
        when: 'Chiron was in {sign} during these periods, between 1900 and 2050:',
        whenNone: 'Chiron does not enter {sign} between 1900 and 2050.',
        present: 'to',
        allSigns: 'Chiron in every sign',
        cta: {
            title: 'Is your Chiron in {sign}?',
            body: 'Your birth chart shows your Chiron sign and house, with a reading to reflect on.',
            button: 'Open the birth chart',
        },
    },
    card: {
        title: '{card} Tarot Card Meaning: Upright, Reversed and Advice | Arcanae',
        description:
            'The meaning of {card} in tarot: upright and reversed meanings, keywords and a question to reflect on. Draw your own card of the day.',
        keywords: 'Keywords',
        upright: 'Upright meaning',
        reversed: 'Reversed meaning',
        advice: 'A question to sit with',
        more: 'Other cards',
        indexTitle:
            'Tarot Card Meanings: All 78 Cards, Upright and Reversed | Arcanae',
        indexDescription:
            'The meaning of every tarot card: the 22 Major Arcana and the four suits of the Minor Arcana, upright and reversed.',
        indexIntro:
            'Every card of the tarot deck, with its upright and reversed meaning. Tap a card to read it, or draw your own card of the day.',
        major: 'Major Arcana',
        suits: {
            wands: 'Wands',
            cups: 'Cups',
            swords: 'Swords',
            pentacles: 'Pentacles',
        },
        cta: {
            title: 'Draw your card of the day',
            body: 'Draw a card and read what it may mean for your day.',
            button: 'Draw a card',
        },
    },
    explore: 'Explore',
    back: 'Back',
};

const uiPt: SeoUi = {
    siteName: 'Arcanae',
    calc: {
        title: 'Calculadora de signo de Quíron',
        date: 'Data de nascimento',
        button: 'Descobrir meu Quíron',
        result: 'Seu Quíron está em',
        retrograde: 'Quíron estava retrógrado no seu nascimento.',
        retrogradeNote:
            'Há quem leia isso como lidar em privado com o ponto sensível; veja o que ressoa em você.',
        edge: 'Quíron mudou de signo perto do seu dia de nascimento, então seu signo pode ser um dos dois. Informe hora e local no mapa completo.',
        range: 'Os dados de Quíron cobrem de 1900 a 2050.',
        sign: 'Sobre Quíron em {sign}',
        moreAbout: 'Saiba mais',
    },
    sign: {
        title: 'Quíron em {sign}: Significado, Datas e Cura | Arcanae',
        description:
            'Quíron em {sign} no mapa astral: a ferida, o dom, os anos em que Quíron esteve em {sign} e como descobrir seu signo de Quíron.',
        h1: 'Quíron em {sign}',
        woundTitle: 'O ponto sensível',
        giftTitle: 'O dom',
        whenTitle: 'Quando Quíron esteve em {sign}?',
        when: 'Quíron esteve em {sign} nestes períodos, entre 1900 e 2050:',
        whenNone: 'Quíron não entra em {sign} entre 1900 e 2050.',
        present: 'a',
        allSigns: 'Quíron em todos os signos',
        cta: {
            title: 'Seu Quíron está em {sign}?',
            body: 'Seu mapa astral mostra o signo e a casa de Quíron, com uma leitura para refletir.',
            button: 'Abrir o mapa astral',
        },
    },
    card: {
        title: 'Significado da Carta {card} no Tarô: Normal, Invertida e Conselho | Arcanae',
        description:
            'O significado de {card} no tarô: sentido normal e invertido, palavras-chave e uma pergunta para refletir. Tire sua carta do dia.',
        keywords: 'Palavras-chave',
        upright: 'Significado normal',
        reversed: 'Significado invertido',
        advice: 'Uma pergunta para ficar com você',
        more: 'Outras cartas',
        indexTitle:
            'Significado das Cartas do Tarô: As 78 Cartas, Normais e Invertidas | Arcanae',
        indexDescription:
            'O significado de cada carta do tarô: os 22 Arcanos Maiores e os quatro naipes dos Arcanos Menores, normais e invertidas.',
        indexIntro:
            'Todas as cartas do baralho de tarô, com o significado normal e invertido. Toque em uma carta para lê-la ou tire sua carta do dia.',
        major: 'Arcanos Maiores',
        suits: {
            wands: 'Paus',
            cups: 'Copas',
            swords: 'Espadas',
            pentacles: 'Ouros',
        },
        cta: {
            title: 'Tire sua carta do dia',
            body: 'Tire uma carta e leia o que ela pode significar para o seu dia.',
            button: 'Tirar uma carta',
        },
    },
    explore: 'Explorar',
    back: 'Voltar',
};

export const seoUi = (language: Language): SeoUi =>
    language === 'pt-BR' ? uiPt : uiEn;

export const fill = (text: string, vars: Record<string, string>): string =>
    text.replace(/\{(\w+)\}/g, (_m, k: string) => vars[k] ?? '');
